import React, { useState } from "react";
import {
  Chart,
  ArcElement,
  Tooltip,
  Legend,
  LegendItem,
  LabelItem } from
"chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Card, Modal, Skeleton } from "antd";
import { Doughnut, Pie } from "react-chartjs-2";
import { HiMiniUserGroup } from "react-icons/hi2";
import { useDispatch, useSelector } from "react-redux";
import {
  AppDispatch,
  getNcdRiskAnalysis,
  getNcdRiskReadingListRequest,
  useDynamicSelector } from
"@src/redux";
import { BASE_URL, colors } from "@src/helpers/constants";
import NcdReadingRiskList from "./NcdReadingRiskList";
import { snakeCase, startCase } from "lodash";

const hexToRgba = (hex: string, alpha: number): string => {
  const cleaned = hex.replace("#", "");
  const bigint = parseInt(
    cleaned.length === 3 ?
    cleaned.split("").map((c) => c + c).join("") :
    cleaned,
    16
  );
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const darkenHex = (hex: string, factor: number): string => {
  const cleaned = hex.replace("#", "");
  const bigint = parseInt(
    cleaned.length === 3 ?
    cleaned.split("").map((c) => c + c).join("") :
    cleaned,
    16
  );
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  const r = clamp(((bigint >> 16) & 255) * factor);
  const g = clamp(((bigint >> 8) & 255) * factor);
  const b = clamp((bigint & 255) * factor);
  return `rgb(${r}, ${g}, ${b})`;
};

const pie3DPlugin = {
  id: "pie3d",
  beforeDatasetDraw(chart: any, args: any) {
    const dataset = chart.data.datasets[args.index];
    if (!dataset || !dataset._pie3d) return;
    const meta = args.meta;
    if (!meta?.data?.length) return;

    const ctx = chart.ctx;
    const depth: number = dataset._pie3dDepth ?? 10;
    const baseColors: string[] = dataset._baseColors || [];

    ctx.save();
    for (let d = depth; d >= 1; d--) {
      meta.data.forEach((arc: any, idx: number) => {
        if (!arc || arc.circumference === 0) return;
        const { x, y, startAngle, endAngle, outerRadius, innerRadius } = arc;
        const baseColor = baseColors[idx] || "#888888";
        const closenessToTop = 1 - d / depth;
        const factor = 0.55 + closenessToTop * 0.2;
        ctx.fillStyle = darkenHex(baseColor, factor);
        ctx.beginPath();
        if (innerRadius > 0) {
          ctx.arc(x, y + d, outerRadius, startAngle, endAngle, false);
          ctx.arc(x, y + d, innerRadius, endAngle, startAngle, true);
        } else {
          ctx.moveTo(x, y + d);
          ctx.arc(x, y + d, outerRadius, startAngle, endAngle);
          ctx.closePath();
        }
        ctx.fill();
      });
    }
    ctx.restore();
  }
};

Chart.register(ArcElement, Tooltip, Legend, ChartDataLabels, pie3DPlugin);

const escapeHtml = (s: string): string =>
String(s).replace(/[&<>"']/g, (c) => ({
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  "\"": "&quot;",
  "'": "&#39;"
} as any)[c]);

const externalTooltipHandler = (context: any) => {
  const { chart, tooltip } = context;
  let tooltipEl = document.getElementById("ncd-chart-tooltip");
  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "ncd-chart-tooltip";
    tooltipEl.className = "ncd-chart-tooltip";
    document.body.appendChild(tooltipEl);
  }

  if (!tooltip || tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  const titleLines: string[] = tooltip.title || [];
  const bodyLines: string[][] = (tooltip.body || []).map((b: any) => b.lines);
  const labelColors: any[] = tooltip.labelColors || [];

  let html = "";
  if (titleLines.length) {
    html += `<div class="ncd-chart-tooltip-title">${titleLines.
    map(escapeHtml).join("<br/>")}</div>`;
  }
  bodyLines.forEach((line, i) => {
    const lc = labelColors[i] || {};
    const dotColor = lc.backgroundColor || "#94a3b8";
    html += `<div class="ncd-chart-tooltip-row">
      <span class="ncd-chart-tooltip-dot" style="background:${escapeHtml(
      String(dotColor))
    }"></span>
      <span class="ncd-chart-tooltip-text">${line.map(escapeHtml).join(" ")}</span>
    </div>`;
  });
  tooltipEl.innerHTML = html;

  const rect = chart.canvas.getBoundingClientRect();
  const margin = 12;
  let left = rect.left + tooltip.caretX + margin;
  let top = rect.top + tooltip.caretY + margin;
  const tipRect = tooltipEl.getBoundingClientRect();
  if (left + tipRect.width + 8 > window.innerWidth) {
    left = rect.left + tooltip.caretX - tipRect.width - margin;
  }
  if (top + tipRect.height + 8 > window.innerHeight) {
    top = rect.top + tooltip.caretY - tipRect.height - margin;
  }
  if (left < 8) left = 8;
  if (top < 8) top = 8;

  tooltipEl.style.opacity = "1";
  tooltipEl.style.left = `${left}px`;
  tooltipEl.style.top = `${top}px`;
};

const buildSliceGradient = (
ctx: CanvasRenderingContext2D,
chartArea: { left: number;right: number;top: number;bottom: number;width: number;height: number; },
baseColor: string)
: CanvasGradient => {
  const cx = (chartArea.left + chartArea.right) / 2;
  const cy = (chartArea.top + chartArea.bottom) / 2;
  const radius = Math.min(chartArea.width, chartArea.height) / 2;
  const gradient = ctx.createRadialGradient(
    cx,
    cy,
    radius * 0.35,
    cx,
    cy,
    radius
  );
  gradient.addColorStop(0, hexToRgba(baseColor, 1));
  gradient.addColorStop(1, hexToRgba(baseColor, 0.78));
  return gradient;
};

const gradientBackgroundFor = (baseColors: string[]): any => (context: any) => {
  const { chart, dataIndex } = context;
  const baseColor = baseColors[dataIndex] || baseColors[0];
  const { ctx, chartArea } = chart || {};
  if (!ctx || !chartArea) return baseColor;
  return buildSliceGradient(ctx, chartArea, baseColor);
};

const gradientHoverBackgroundFor = (baseColors: string[]): any => (
context: any) =>
{
  const { chart, dataIndex } = context;
  const baseColor = baseColors[dataIndex] || baseColors[0];
  const { ctx, chartArea } = chart || {};
  if (!ctx || !chartArea) return baseColor;
  const cx = (chartArea.left + chartArea.right) / 2;
  const cy = (chartArea.top + chartArea.bottom) / 2;
  const radius = Math.min(chartArea.width, chartArea.height) / 2;
  const gradient = ctx.createRadialGradient(
    cx,
    cy,
    radius * 0.3,
    cx,
    cy,
    radius
  );
  gradient.addColorStop(0, hexToRgba(baseColor, 1));
  gradient.addColorStop(1, hexToRgba(baseColor, 0.92));
  return gradient;
};

interface LegendEntry {
  label: string;
  value: number;
  color: string;
}

const formatNumber = (n: number) =>
  Number.isFinite(n) ? n.toLocaleString("en-US") : "0";

const AnalysisLegend: React.FC<{
  entries: LegendEntry[];
  total: number;
  layout?: "list" | "grid";
}> = ({ entries, total, layout = "list" }) => {
  const className =
  layout === "grid" ?
  "ncd-analysis-legend ncd-analysis-legend--grid" :
  "ncd-analysis-legend";
  const showBar = layout !== "grid";
  return (
    <ul className={className}>
      {entries.map((entry, idx) => {
        const pct = total > 0 ? (entry.value / total) * 100 : 0;
        return (
          <li className="ncd-analysis-legend-item" key={idx}>
            <span
              className="ncd-legend-dot"
              style={{ backgroundColor: entry.color }} />

            <div className="ncd-legend-content">
              <div className="ncd-legend-row">
                <span className="ncd-legend-label" title={entry.label}>
                  {entry.label}
                </span>
                <span className="ncd-legend-value">
                  {formatNumber(entry.value)}
                  <small className="ncd-legend-percent">
                    {pct.toFixed(pct >= 10 || pct === 0 ? 0 : 1)}%
                  </small>
                </span>
              </div>
              {showBar &&
              <div className="ncd-legend-bar">
                  <div
                  className="ncd-legend-bar-fill"
                  style={{
                    width: `${Math.min(pct, 100)}%`,
                    backgroundColor: entry.color
                  }} />

                </div>
              }
            </div>
          </li>);

      })}
    </ul>);

};

interface RiskData {
  labels: string[];
  values: number[];
  backgroundColor: string[];
  legends?: any;
}

interface CardData {
  title: string;
  data: RiskData;
  showIcon: boolean;
}

const NCDRiskAnalysis: React.FC = () => {
  const {
    result: getNcdRiskAnalysisList,
    loading: getNcdRiskAnalysisLoading
  } = useSelector(getNcdRiskAnalysis);

  const [readingRiskModal, setReadingRiskModal] = useState<boolean>(false);
  const [ncdRiskType, setNcdRiskType] = useState<string>();

  const [readingRiskDetails, setReadingRiskDerails] = useState<
    CardData | undefined>(
  );
  const { ncdRiskFilter } = useDynamicSelector("ncd_risk_analysis");
  const dispatch = useDispatch<AppDispatch>();

  const CARD_DATA: CardData[] = [
  {
    title: "Hypertension Risk",
    data: {
      labels: [
      `N: ${getNcdRiskAnalysisList?.noHypertensionRiskCount || 0}`,
      `A: ${getNcdRiskAnalysisList?.hypertensionRiskCount || 0}`],

      values: [
      getNcdRiskAnalysisList?.noHypertensionRiskCount || 0,
      getNcdRiskAnalysisList?.hypertensionRiskCount || 0],

      backgroundColor: [colors.veryGood, colors.veryBad],
      legends: [
      {
        datasetIndex: 0,
        index: 0,
        text: `N: ${getNcdRiskAnalysisList?.noHypertensionRiskCount}`,
        fillStyle: colors.veryGood,
        strokeStyle: "#fff",
        lineWidth: 2
      },
      {
        datasetIndex: 1,
        index: 1,
        text: `A: ${getNcdRiskAnalysisList?.hypertensionRiskCount}`,
        fillStyle: colors.veryBad,
        strokeStyle: "#fff",
        lineWidth: 2
      }]

    },
    showIcon:
    getNcdRiskAnalysisList &&
    Number(getNcdRiskAnalysisList?.hypertensionRiskCount) > 0 ?
    true :
    false
  },
  {
    title: "Diabetic Risk",
    data: {
      labels: [
      `N: ${getNcdRiskAnalysisList?.noDiabeticRiskCount || 0}`,
      `A: ${getNcdRiskAnalysisList?.diabeticRiskCount || 0}`],

      values: [
      getNcdRiskAnalysisList?.noDiabeticRiskCount || 0,
      getNcdRiskAnalysisList?.diabeticRiskCount || 0],

      backgroundColor: [colors.veryGood, colors.veryBad],
      legends: [
      {
        datasetIndex: 0,
        index: 0,
        text: `N: ${getNcdRiskAnalysisList?.noDiabeticRiskCount}`,
        fillStyle: colors.veryGood,
        strokeStyle: "#fff",
        lineWidth: 2
      },
      {
        datasetIndex: 1,
        index: 1,
        text: `A: ${getNcdRiskAnalysisList?.diabeticRiskCount}`,
        fillStyle: colors.veryBad,
        strokeStyle: "#fff",
        lineWidth: 2
      }]

    },
    showIcon:
    getNcdRiskAnalysisList &&
    Number(getNcdRiskAnalysisList?.diabeticRiskCount) > 0 ?
    true :
    false
  },
  {
    title: "Obesity Risk",
    data: {
      labels: [
      `N: ${getNcdRiskAnalysisList?.noObesityRiskCount || 0}`,
      `A: ${getNcdRiskAnalysisList?.obesityRiskCount || 0}`],

      values: [
      getNcdRiskAnalysisList?.noObesityRiskCount || 0,
      getNcdRiskAnalysisList?.obesityRiskCount || 0],

      backgroundColor: [colors.veryGood, colors.veryBad],
      legends: [
      {
        datasetIndex: 0,
        index: 0,
        text: `N: ${getNcdRiskAnalysisList?.noObesityRiskCount}`,
        fillStyle: colors.veryGood,
        strokeStyle: "#fff",
        lineWidth: 2
      },
      {
        datasetIndex: 1,
        index: 1,
        text: `A: ${getNcdRiskAnalysisList?.obesityRiskCount}`,
        fillStyle: colors.veryBad,
        strokeStyle: "#fff",
        lineWidth: 2
      }]

    },
    showIcon:
    getNcdRiskAnalysisList &&
    Number(getNcdRiskAnalysisList?.obesityRiskCount) > 0 ?
    true :
    false
  },
  {
    title: "Hypotension Risk",
    data: {
      labels: [
      `N: ${getNcdRiskAnalysisList?.noHypotensionRiskCount || 0}`,
      `A: ${getNcdRiskAnalysisList?.hypotensionRiskCount || 0}`],

      values: [
      getNcdRiskAnalysisList?.noHypotensionRiskCount || 0,
      getNcdRiskAnalysisList?.hypotensionRiskCount || 0],

      backgroundColor: [colors.veryGood, colors.veryBad],
      legends: [
      {
        datasetIndex: 0,
        index: 0,
        text: `N: ${getNcdRiskAnalysisList?.noHypotensionRiskCount}`,
        fillStyle: colors.veryGood,
        strokeStyle: "#fff",
        lineWidth: 2
      },
      {
        datasetIndex: 1,
        index: 1,
        text: `A: ${getNcdRiskAnalysisList?.hypotensionRiskCount}`,
        fillStyle: colors.veryBad,
        strokeStyle: "#fff",
        lineWidth: 2
      }]

    },
    showIcon:
    getNcdRiskAnalysisList &&
    Number(getNcdRiskAnalysisList?.hypotensionRiskCount) > 0 ?
    true :
    false
  },
  {
    title: "Cardiac Risk",
    data: {
      labels: [
      `N: ${getNcdRiskAnalysisList?.noCardiacRiskCount || 0}`,
      `A: ${getNcdRiskAnalysisList?.cardiacRiskCount || 0}`],

      values: [
      getNcdRiskAnalysisList?.noCardiacRiskCount || 0,
      getNcdRiskAnalysisList?.cardiacRiskCount || 0],

      backgroundColor: [colors.veryGood, colors.veryBad],
      legends: [
      {
        datasetIndex: 0,
        index: 0,
        text: `N: ${getNcdRiskAnalysisList?.noCardiacRiskCount}`,
        fillStyle: colors.veryGood,
        strokeStyle: "#fff",
        lineWidth: 2
      },
      {
        datasetIndex: 1,
        index: 1,
        text: `A: ${getNcdRiskAnalysisList?.cardiacRiskCount}`,
        fillStyle: colors.veryBad,
        strokeStyle: "#fff",
        lineWidth: 2
      }]

    },
    showIcon:
    getNcdRiskAnalysisList &&
    Number(getNcdRiskAnalysisList?.cardiacRiskCount) > 0 ?
    true :
    false
  },
  {
    title: "Respiratory Risk",
    data: {
      labels: [
      `N: ${getNcdRiskAnalysisList?.noRespiratoryRiskCount || 0}`,
      `A: ${getNcdRiskAnalysisList?.respiratoryRiskCount || 0}`],

      values: [
      getNcdRiskAnalysisList?.noRespiratoryRiskCount || 0,
      getNcdRiskAnalysisList?.respiratoryRiskCount || 0],

      backgroundColor: [colors.veryGood, colors.veryBad],
      legends: [
      {
        datasetIndex: 0,
        index: 0,
        text: `N: ${getNcdRiskAnalysisList?.noRespiratoryRiskCount}`,
        fillStyle: colors.veryGood,
        strokeStyle: "#fff",
        lineWidth: 2
      },
      {
        datasetIndex: 1,
        index: 1,
        text: `A: ${getNcdRiskAnalysisList?.respiratoryRiskCount}`,
        fillStyle: colors.veryBad,
        strokeStyle: "#fff",
        lineWidth: 2
      }]

    },
    showIcon:
    getNcdRiskAnalysisList &&
    Number(getNcdRiskAnalysisList?.respiratoryRiskCount) > 0 ?
    true :
    false
  }];

  const largeChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "70%",
    layout: {
      padding: 14
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 900,
      easing: "easeOutQuart"
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false,
        external: externalTooltipHandler,
        callbacks: {
          label: (ctx: any) => {
            const dataArr = ctx.dataset.data as number[];
            const total = dataArr.reduce(
              (a: number, b: number) => a + Number(b || 0),
              0
            );
            const value = Number(ctx.parsed || 0);
            const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0";
            return `${ctx.label || ""}: ${value.toLocaleString()} (${pct}%)`;
          }
        }
      },
      datalabels: {
        color: "#ffffff",
        textAlign: "center" as const,
        font: {
          family: "Abel",
          weight: 700 as const,
          size: 12
        },
        textStrokeColor: "rgba(15, 23, 42, 0.45)",
        textStrokeWidth: 2,
        textShadowBlur: 4,
        textShadowColor: "rgba(15, 23, 42, 0.35)",
        formatter: (value: number, ctx: any) => {
          const dataArr = ctx.dataset.data as number[];
          const total = dataArr.reduce(
            (a: number, b: number) => a + Number(b || 0),
            0
          );
          if (!value || total === 0) return "";
          const pct = (value / total) * 100;
          if (pct < 5) return "";
          return `${pct.toFixed(pct >= 10 ? 0 : 1)}%`;
        }
      }
    }
  };

  const compactOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "62%",
    layout: {
      padding: 8
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 700,
      easing: "easeOutQuart"
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false,
        external: externalTooltipHandler,
        callbacks: {
          label: (ctx: any) => {
            const dataArr = ctx.dataset.data as number[];
            const total = dataArr.reduce(
              (a: number, b: number) => a + Number(b || 0),
              0
            );
            const value = Number(ctx.parsed || 0);
            const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0";
            return `${ctx.label || ""}: ${value.toLocaleString()} (${pct}%)`;
          }
        }
      },
      datalabels: {
        display: false
      }
    }
  };

  const processData = (data: RiskData) => {
    const values = data.values || [];
    return {
      labels: data.labels,
      datasets: [
      {
        data: values,
        backgroundColor: gradientBackgroundFor(data.backgroundColor),
        hoverBackgroundColor: gradientHoverBackgroundFor(data.backgroundColor),
        borderColor: "#ffffff",
        borderWidth: 0,
        hoverBorderColor: "#ffffff",
        hoverBorderWidth: 0,
        hoverOffset: 0,
        borderRadius: 0,
        spacing: 0
      }]

    } as any;
  };

  const riskCountValues = [
  Number(getNcdRiskAnalysisList?.zeroRiskCount || 0),
  Number(getNcdRiskAnalysisList?.oneRiskCount || 0),
  Number(getNcdRiskAnalysisList?.twoRiskCount || 0),
  Number(getNcdRiskAnalysisList?.threeRiskCount || 0),
  Number(getNcdRiskAnalysisList?.fourRiskCount || 0),
  Number(getNcdRiskAnalysisList?.fiveRiskCount || 0)];

  const riskCountColors = [
  colors.excellent,
  colors.veryGood,
  colors.good,
  colors.bad,
  colors.veryBad,
  colors.poor];

  const riskCountLabels = [
  "No Risk",
  "1 Risk",
  "2 Risks",
  "3 Risks",
  "4 Risks",
  "5 Risks"];


  const riskCount: any = {
    datasets: [
    {
      data: riskCountValues,
      backgroundColor: gradientBackgroundFor(riskCountColors),
      hoverBackgroundColor: gradientHoverBackgroundFor(riskCountColors),
      borderWidth: 0,
      borderColor: "#ffffff",
      hoverBorderColor: "#ffffff",
      hoverBorderWidth: 0,
      hoverOffset: 0,
      borderRadius: 0,
      spacing: 0
    }],

    labels: riskCountLabels
  };

  const totalReadings = Number(getNcdRiskAnalysisList?.totalReadingCount || 0);
  const atRiskCount = Number(getNcdRiskAnalysisList?.riskCount || 0);
  const normalReadings = Math.max(totalReadings - atRiskCount, 0);
  const overallAnalysisLegend: LegendEntry[] = [
  {
    label: "Normal Readings",
    value: normalReadings,
    color: colors.veryGood
  },
  {
    label: "At Risk",
    value: atRiskCount,
    color: colors.veryBad
  }];

  const riskCountTotal = riskCountValues.reduce((a, b) => a + b, 0);
  const riskCountLegend: LegendEntry[] = riskCountLabels.map(
    (label, idx) => ({
      label,
      value: riskCountValues[idx],
      color: riskCountColors[idx]
    })
  );

  const handleRiskDetails = (item: any) => {
    setReadingRiskModal(true);
    setReadingRiskDerails(item);
    setNcdRiskType(snakeCase(item.title).replace("_risk", ""));
    dispatch(
      getNcdRiskReadingListRequest({
        filter: {
          ...ncdRiskFilter,
          riskType: snakeCase(item.title).replace("_risk", "")
        },
        pagination: {
          pageNumber: 1,
          pageLimit: 10
        }
      })
    );
  };
  const handleClose = () => {
    setReadingRiskModal(false);
    setReadingRiskDerails(undefined);
  };

  return (
    <section className="home-section">
      <div className="home-section-header">
        <span className="home-section-accent" />
        <h2 className="home-section-title">Non-Communicable Disease (NCD)</h2>
      </div>
      <div className="risk-analysis-card-container">
        <div className="ncd-analysis-card">
          <div className="ncd-analysis-card-head">
            <h3 className="ncd-analysis-card-title">Overall Analysis</h3>
            {atRiskCount > 0 &&
            <button
              type="button"
              className="ncd-card-action ncd-card-action--large"
              onClick={() =>
                handleRiskDetails({ title: "Over_All_Analysis" })}
              aria-label="View Overall Analysis"
            >
                <HiMiniUserGroup size={14} />
              </button>
            }
          </div>
          <div className="ncd-analysis-card-body">
            <div className="ncd-analysis-donut">
              {getNcdRiskAnalysisLoading ?
              <Skeleton.Avatar shape="circle" active size={160} /> :

              <>
                  <span className="ncd-analysis-donut-glow" aria-hidden />
                  <Doughnut
                  data={{
                    labels: ["Normal Readings", "At Risk"],
                    datasets: [
                    {
                      data: [normalReadings, atRiskCount],
                      backgroundColor: gradientBackgroundFor([
                      colors.veryGood,
                      colors.veryBad]
                      ),
                      hoverBackgroundColor: gradientHoverBackgroundFor([
                      colors.veryGood,
                      colors.veryBad]
                      ),
                      borderWidth: 0,
                      borderColor: "#ffffff",
                      hoverBorderColor: "#ffffff",
                      hoverBorderWidth: 0,
                      hoverOffset: 0,
                      borderRadius: 0,
                      spacing: 0
                    }]

                  } as any}
                  options={largeChartOptions} />

                  <div className="ncd-analysis-donut-center">
                    <span className="ncd-analysis-donut-value">
                      {formatNumber(totalReadings)}
                    </span>
                    <span className="ncd-analysis-donut-label">
                      Total Readings
                    </span>
                  </div>
                </>
              }
            </div>
            {getNcdRiskAnalysisLoading ?
            <div className="ncd-analysis-legend ncd-analysis-legend--loading">
                <Skeleton active paragraph={{ rows: 2 }} title={false} />
              </div> :

            <AnalysisLegend
              entries={overallAnalysisLegend}
              total={totalReadings} />

            }
          </div>
        </div>
        <div className="ncd-analysis-card">
          <div className="ncd-analysis-card-head">
            <h3 className="ncd-analysis-card-title">Risk Count Analysis</h3>
            {riskCountTotal > 0 &&
            <button
              type="button"
              className="ncd-card-action ncd-card-action--large"
              onClick={() =>
                handleRiskDetails({ title: "risk_count_analysis" })}
              aria-label="View Risk Count Analysis"
            >
                <HiMiniUserGroup size={14} />
              </button>
            }
          </div>
          <div className="ncd-analysis-card-body">
            <div className="ncd-analysis-donut">
              {getNcdRiskAnalysisLoading ?
              <Skeleton.Avatar shape="circle" active size={160} /> :

              <>
                  <span className="ncd-analysis-donut-glow" aria-hidden />
                  <Doughnut data={riskCount} options={largeChartOptions} />
                  <div className="ncd-analysis-donut-center">
                    <span className="ncd-analysis-donut-value">
                      {formatNumber(riskCountTotal)}
                    </span>
                    <span className="ncd-analysis-donut-label">
                      Total Subjects
                    </span>
                  </div>
                </>
              }
            </div>
            {getNcdRiskAnalysisLoading ?
            <div className="ncd-analysis-legend ncd-analysis-legend--loading">
                <Skeleton active paragraph={{ rows: 4 }} title={false} />
              </div> :

            <AnalysisLegend
              entries={riskCountLegend}
              total={riskCountTotal}
              layout="grid" />

            }
          </div>
        </div>
      </div>
      <div className="ncd-risk-secondary-container">
        {CARD_DATA.map((item, index) => {
          const normalCount = item.data.values[0] || 0;
          const riskCount = item.data.values[1] || 0;
          const total = normalCount + riskCount;
          const riskPct =
            total > 0 ? ((riskCount / total) * 100).toFixed(1) : null;

          return (
            <div key={index} className="ncd-card">
              <div className="ncd-card-head">
                <h4 className="ncd-card-title" title={item.title}>
                  {item.title}
                </h4>
                {item.showIcon && (
                  <button
                    type="button"
                    className="ncd-card-action"
                    onClick={() => handleRiskDetails(item)}
                    aria-label={`View ${item.title} details`}
                  >
                    <HiMiniUserGroup size={13} />
                  </button>
                )}
              </div>

              <div className="ncd-card-body">
                <div className="ncd-card-chart ncd-card-chart--ring">
                  {getNcdRiskAnalysisLoading ? (
                    <Skeleton.Avatar shape="circle" active size={76} />
                  ) : (
                    <>
                      <Doughnut
                        data={processData(item?.data)}
                        options={compactOptions}
                      />
                      <div className="ncd-card-chart-center">
                        <span className="ncd-card-chart-center-value">
                          {riskPct && Number(riskPct) > 0
                            ? `${riskPct}%`
                            : "0%"}
                        </span>
                        <span className="ncd-card-chart-center-label">
                          At Risk
                        </span>
                      </div>
                    </>
                  )}
                </div>

                <div className="ncd-card-stats">
                  <div className="ncd-card-stat">
                    <span className="ncd-card-dot ncd-card-dot--normal" />
                    <span className="ncd-card-stat-label">Normal</span>
                    <span className="ncd-card-stat-value">{normalCount}</span>
                  </div>
                  <div className="ncd-card-stat">
                    <span className="ncd-card-dot ncd-card-dot--risk" />
                    <span className="ncd-card-stat-label">At risk</span>
                    <span className="ncd-card-stat-value">
                      {riskCount}
                      {riskPct && Number(riskPct) > 0 && (
                        <small className="ncd-card-stat-pct">
                          {riskPct}%
                        </small>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <Modal
        title={
        readingRiskDetails &&
        `${startCase(readingRiskDetails?.title.replace("Risk", "Abnormal Readings"))}`}

        open={readingRiskModal}
        onCancel={handleClose}
        footer={null}
        width={1100}
        className="ncd-risk-modal">

        <NcdReadingRiskList ncdRiskType={ncdRiskType} />
      </Modal>
    </section>);

};

export default NCDRiskAnalysis;